// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule, ViewportScroller } from '@angular/common';
import {
  AfterViewChecked,
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import {
  Observable,
  Subject,
  combineLatest,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs';
import { ExtendedCoilModel } from 'src/app/_interfaces/coil/coil-extension-model';
import { CoilExternalLinks } from 'src/app/_interfaces/coil/coil-external-link-model';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { CoilService } from 'src/app/modules/coils/services/coil.service';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { NoDataIconComponent } from 'src/app/modules/shared/components/no-data-icon/no-data-icon.component';
import { LocalizedDatePipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import { DateRangeService } from '../../services/date-range/date-range.service';
import { CoilDetailComponent } from '../coil-detail/coil-detail.component';

interface Coil {
  coilMaterialNumber: string;
  coilSerialNumber: string;
}

interface CoilData {
  calDay: string;
}

interface DateRange {
  firstDate: moment.Moment;
  lastDate: moment.Moment;
}

@Component({
  selector: 'app-coils',
  templateUrl: './coils.component.html',
  styleUrls: ['./coils.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    LocalizedDatePipe,
    APICallGateComponent,
    CoilDetailComponent,
    CommonModule,
    TranslateModule,
    NoDataIconComponent,
  ],
})
export class CoilsComponent implements OnChanges, OnDestroy, AfterViewChecked {
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  externalLinks!: CoilExternalLinks[];
  systemCoils$ = this.coilService.getSystemCoils.lastSuccessfulData$;
  expandedCoilSerialNumber: string | null = null;

  invertDatesTimelineSubject$ = this.settingsService.timelineAscending$;

  private coilMarkers = this.coilService.getCoilMarkers;
  private coils = this.coilService.getSystemCoils;
  private destroy$ = new Subject<void>();
  private dateRangeService = inject(DateRangeService);

  constructor(
    protected coilService: CoilService,
    private settingsService: SettingsService,
    protected screenLayoutService: ScreenLayoutService,
    private viewportScroller: ViewportScroller
  ) {}

  ngOnChanges(): void {
    this.getCoilsBySystemSerialNumber();
    this.getCoilMarkers();
    this.externalLinks = this.coilService.getExternalLinks();
  }

  ngAfterViewChecked(): void {
    this.getScrollPosition();
    this.geExpandedCoil();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // daterange should be derived as min-max date from all coil histories
  protected dateRange$: Observable<DateRange> =
    this.coils.lastSuccessfulData$.pipe(
      mergeMap((coils: ReadonlyArray<Readonly<Coil>>) =>
        this.getCoilDataObservables(coils as Coil[])
      ),
      map((coils: CoilData[][]) => this.calculateDateRange(coils))
    );

  private getCoilDataObservables(coils: Coil[]): Observable<CoilData[][]> {
    return combineLatest(
      coils.map((coil: Coil) => this.getCoilDataObservable(coil))
    );
  }
  private getCoilDataObservable(coil: Coil): Observable<CoilData[]> {
    const params = {
      systemSerialNumber: this.systemDetails?.systemSerialNumber ?? '',
      systemMaterialNumber: this.systemDetails?.systemMaterialNumber ?? '',
      coilMaterialNumber: coil?.coilMaterialNumber ?? '',
      coilSerialNumber: +coil?.coilSerialNumber ? +coil.coilSerialNumber : 0, // API SPEC IS WRONG! COIL SNR CAN BE STRING AFAIK
    };

    const endpoint = this.coilService.mapCoilHistoryEndpoint(params);
    if (endpoint.request) {
      endpoint.request(params);
    }
    return endpoint.lastSuccessfulData$ as unknown as Observable<CoilData[]>;
  }

  private calculateDateRange(coils: CoilData[][]): DateRange {
    const anyDate = coils[0][0].calDay;
    let firstDate = moment(anyDate);
    let lastDate = moment(anyDate);
    coils.forEach((days: CoilData[]) =>
      days.forEach(({ calDay }: CoilData) => {
        const day = moment(calDay);
        firstDate = moment.min([firstDate, day]);
        lastDate = moment.max([lastDate, day]);
        this.setDateRangeServiceDates(firstDate, lastDate);
      })
    );
    return {
      firstDate,
      lastDate,
    };
  }

  setDateRangeServiceDates(
    firstDate: moment.Moment,
    lastDate: moment.Moment
  ): void {
    this.dateRangeService.setDateRange({ firstDate, lastDate });
  }

  reversesTheDates$ = this.dateRange$.pipe(
    map(({ firstDate, lastDate }) => ({
      firstDate: lastDate,
      lastDate: firstDate,
    }))
  );

  reversedDates$ = this.invertDatesTimelineSubject$.pipe(
    switchMap((isDateReversed) =>
      isDateReversed ? this.dateRange$ : this.reversesTheDates$
    ),
    tap((res) => {
      this.dateRangeService.setReversedDateRange(res);
    })
  );

  toggleCoilExpansion(coilSerialNumber: string): void {
    this.expandedCoilSerialNumber =
      this.expandedCoilSerialNumber === coilSerialNumber
        ? null
        : coilSerialNumber;
  }

  addExternalItems(coils: ExtendedCoilModel[]): ExtendedCoilModel[] {
    coils.forEach((coil) => {
      coil.isExpandCoil = false;
    });

    return coils;
  }

  private geExpandedCoil(): void {
    const expandedCoilSerialNumber = sessionStorage.getItem(
      'expandedCoilSerialNumber'
    );
    if (expandedCoilSerialNumber) {
      this.expandedCoilSerialNumber = expandedCoilSerialNumber;
      setTimeout(() => {
        sessionStorage.removeItem('expandedCoilSerialNumber');
      }, 500);
    }
  }

  private getScrollPosition(): void {
    const currentPosition = sessionStorage.getItem('scrollPosition');
    if (currentPosition) {
      this.viewportScroller.scrollToPosition([0, +currentPosition]);
      setTimeout(() => {
        sessionStorage.removeItem('scrollPosition');
      }, 500);
    }
  }

  private getCoilsBySystemSerialNumber(): void {
    const { systemSerialNumber = '', systemMaterialNumber = '' } =
      this.systemDetails ?? {};

    if (systemSerialNumber && systemMaterialNumber) {
      this.coils.request({
        systemSerialNumber: systemSerialNumber,
        systemMaterialNumber: systemMaterialNumber,
      });
    } else {
      console.error('System serial number or material number is undefined');
    }
  }

  private getCoilMarkers(): void {
    this.coilMarkers.request();
  }
}
